import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Clock, CheckCircle, AlertCircle, ArrowLeft, ArrowRight, Send } from 'lucide-react';
import axios from 'axios';

const Quiz = () => {
  const { quizId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  useEffect(() => {
    if (quiz && quiz.duration && startTime) {
      const timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const remaining = (quiz.duration * 60) - elapsed;
        setTimeLeft(Math.max(0, remaining));

        if (remaining <= 0) {
          handleSubmitQuiz();
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [quiz, startTime]);

  const fetchQuiz = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Try to fetch existing submission first
      const submissionResponse = await axios.get(`/quizzes/${quizId}/my-submission`)
        .catch(() => ({ data: null }));

      if (submissionResponse.data) {
        setResults(submissionResponse.data);
        setShowResults(true);

        // If submission exists, we still need basic quiz info for the results header
        // Since we removed the attempt limit check on /questions, we can fetch it safely
        const quizResponse = await axios.get(`/quizzes/${quizId}/questions`);
        setQuiz(quizResponse.data);
        setLoading(false);
        return;
      }

      // 2. If no submission, fetch quiz questions for taking
      const quizResponse = await axios.get(`/quizzes/${quizId}/questions`);
      setQuiz(quizResponse.data);
      setStartTime(Date.now());

      // Initialize answers object
      const initialAnswers = {};
      quizResponse.data.questions.forEach(question => {
        initialAnswers[question.id] = '';
      });
      setAnswers(initialAnswers);

      // Set time limit if duration is specified
      if (quizResponse.data.duration) {
        setTimeLeft(quizResponse.data.duration * 60);
      }
    } catch (error) {
      console.error('Error fetching quiz:', error);
      setError(error.response?.data?.message || 'Failed to load quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);

      const timeSpent = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;

      const response = await axios.post('/quizzes/quiz-submissions', {
        quizId: parseInt(quizId),
        answers,
        timeSpent
      });

      setResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setError(error.response?.data?.message || 'Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionStatus = (index) => {
    const question = quiz.questions[index];
    const answer = answers[question.id];
    return answer ? 'answered' : 'unanswered';
  };

  const renderQuestion = (question) => {
    const answer = answers[question.id];

    // Helper function to parse options - handles both string and array formats
    const parseOptions = (options) => {
      if (!options) return [];
      if (Array.isArray(options)) return options;
      if (typeof options === 'string') {
        // Handle comma-separated string format
        return options.split(',').map(opt => opt.trim()).filter(opt => opt);
      }
      try {
        // Try to parse as JSON
        return JSON.parse(options);
      } catch {
        // If JSON parsing fails, treat as comma-separated string
        return options.split(',').map(opt => opt.trim()).filter(opt => opt);
      }
    };

    switch (question.type) {
      case 'multiple_choice':
        const options = parseOptions(question.options);
        return (
          <div className="space-y-3">
            {options.map((option, index) => (
              <label key={index} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={answer === option}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'true_false':
        return (
          <div className="space-y-3">
            {['true', 'false'].map((option) => (
              <label key={option} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={answer === option}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-gray-700 capitalize">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'short_answer':
        return (
          <textarea
            value={answer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder="Enter your answer here..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={4}
          />
        );

      default:
        return <p className="text-gray-500">Unsupported question type</p>;
    }
  };

  const renderDetailedReview = () => {
    if (!results || !results.quiz || !results.quiz.questions) return null;

    const studentAnswers = typeof results.answers === 'string'
      ? JSON.parse(results.answers)
      : (results.answers || {});

    return (
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-8">
        <h3 className="text-xl font-bold text-gray-900 border-b pb-4">Detailed Question Review</h3>
        {results.quiz.questions.map((question, index) => {
          const studentAnswer = studentAnswers[question.id];
          const isCorrect = question.type === 'short_answer' ? null : (String(studentAnswer || '').toLowerCase() === String(question.correctAnswer || '').toLowerCase());

          return (
            <div key={question.id} className="border-b last:border-0 pb-6 last:pb-0">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-900">
                  Question {index + 1}: {question.text}
                </h4>
                {isCorrect !== null && (
                  <span className={`px-2 py-1 rounded text-xs font-bold ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {isCorrect ? 'CORRECT' : 'INCORRECT'}
                  </span>
                )}
                {question.type === 'short_answer' && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-bold">
                    GRADED
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase font-bold mb-1">Your Answer</p>
                  <p className={studentAnswer ? "text-gray-900" : "text-gray-400 italic"}>
                    {studentAnswer || 'No answer provided'}
                  </p>
                </div>

                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-500 uppercase font-bold mb-1">Correct Answer</p>
                  <p className="text-blue-900">
                    {question.correctAnswer || 'N/A'}
                  </p>
                </div>
              </div>

              {question.explanation && (
                <div className="mt-3 bg-amber-50 p-3 rounded-lg text-sm">
                  <p className="text-amber-800"><span className="font-bold">Explanation:</span> {question.explanation}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-error-800 mb-2">Error Loading Quiz</h3>
        <p className="text-error-700 mb-4">{error}</p>
        <button
          onClick={() => navigate('/assignments')}
          className="btn-primary"
        >
          Back to Assignments
        </button>
      </div>
    );
  }

  if (showResults && results) {
    const passed = Number(results.percentage || 0) >= (quiz.passingScore || 0);

    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${passed ? 'bg-green-100' : 'bg-red-100'
              }`}>
              {passed ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <AlertCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Quiz Completed!</h1>
            <p className="text-gray-600">{quiz?.title}</p>
          </div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border p-6 text-center">
            <div className="text-3xl font-bold text-primary-600 mb-2">
              {results.score}/{results.totalPoints}
            </div>
            <p className="text-gray-600">Score</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 text-center">
            <div className="text-3xl font-bold text-primary-600 mb-2">
              {Number(results.percentage || 0).toFixed(1)}%
            </div>
            <p className="text-gray-600">Percentage</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 text-center">
            <div className={`text-3xl font-bold mb-2 ${passed ? 'text-green-600' : 'text-red-600'
              }`}>
              {passed ? 'PASSED' : 'FAILED'}
            </div>
            <p className="text-gray-600">Status</p>
          </div>
        </div>

        {/* Feedback Section */}
        {results.feedback && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Lecturer Feedback</h3>
            <p className="text-blue-800 whitespace-pre-wrap">{results.feedback}</p>
          </div>
        )}

        {/* Detailed Review Section */}
        {renderDetailedReview()}

        {/* Attempt Info */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attempt Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Attempt Number</p>
              <p className="font-medium">{results.attemptNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Time Spent</p>
              <p className="font-medium">{formatTime(results.timeSpent)}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => navigate('/assignments')}
            className="btn-secondary"
          >
            Back to Assignments
          </button>
          {results.attemptNumber < (quiz?.maxAttempts || 0) && !passed && (
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!quiz) return null;


  const currentQuestion = quiz.questions[currentQuestionIndex];
  const answeredCount = Object.values(answers).filter(answer => answer !== '').length;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/assignments')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Assignments</span>
          </button>

          {timeLeft !== null && (
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-gray-500" />
              <span className={`font-mono text-lg ${timeLeft < 300 ? 'text-red-600' : 'text-gray-900'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          )}
        </div>

        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{quiz.title}</h1>
          {quiz.description && (
            <p className="text-gray-600 mb-4">{quiz.description}</p>
          )}

          <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
            <span>Question {currentQuestionIndex + 1} of {quiz.questions.length}</span>
            <span>{answeredCount} answered</span>
            <span>Attempt {quiz.maxAttempts ? `1/${quiz.maxAttempts}` : '1'}</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestionIndex + 1) / quiz.questions.length) * 100}%` }}
          ></div>
        </div>

        {/* Question Navigation */}
        <div className="flex flex-wrap gap-2">
          {quiz.questions.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentQuestionIndex(index)}
              className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${index === currentQuestionIndex
                ? 'bg-primary-600 text-white'
                : getQuestionStatus(index) === 'answered'
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Question {currentQuestionIndex + 1}
          </h2>
          <p className="text-gray-700">{currentQuestion.text}</p>
          {currentQuestion.points && (
            <p className="text-sm text-gray-500 mt-2">Points: {currentQuestion.points}</p>
          )}
        </div>

        {/* Answer Options */}
        <div className="mb-6">
          {renderQuestion(currentQuestion)}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <div className="flex space-x-3">
            {currentQuestionIndex === quiz.questions.length - 1 ? (
              <button
                onClick={handleSubmitQuiz}
                disabled={submitting || answeredCount === 0}
                className="flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span>{submitting ? 'Submitting...' : 'Submit Quiz'}</span>
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <span>Next</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Instructions:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Answer all questions to the best of your ability</li>
          <li>• You can navigate between questions using the buttons or question numbers</li>
          <li>• Your answers are automatically saved as you go</li>
          {quiz.duration && <li>• Complete the quiz within the time limit</li>}
          <li>• You have {quiz.maxAttempts || 1} attempt{quiz.maxAttempts !== 1 ? 's' : ''} for this quiz</li>
        </ul>
      </div>
    </div>
  );
};

export default Quiz;
